"use client";

import React from "react";
import { useState, useRef, useEffect } from "react";
import { cn } from "@utils/utils";
import styles from "./Dropdown.module.scss";

interface DropdownProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  align?: "left" | "right";
  className?: string;
}

export function Dropdown({
  trigger,
  children,
  align = "left",
  className,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => setIsOpen(!isOpen);
  const closeDropdown = () => setIsOpen(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={styles.dropdown} ref={dropdownRef}>
      <div className={styles.dropdown__trigger} onClick={toggleDropdown}>
        {trigger}
      </div>
      {isOpen && (
        <div
          className={cn(
            styles.dropdown__content,
            align === "right"
              ? styles["dropdown__content--right"]
              : styles["dropdown__content--left"],
            className
          )}
        >
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child) && child.type === DropdownItem) {
              return React.cloneElement(
                child as React.ReactElement<DropdownItemProps>,
                {
                  onClose: closeDropdown,
                }
              );
            }
            return child;
          })}
        </div>
      )}
    </div>
  );
}

interface DropdownItemProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  onClose?: () => void;
}

export function DropdownItem({
  children,
  className,
  onClose,
  onClick,
  ...props
}: DropdownItemProps) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    onClose?.();
  };

  return (
    <button
      className={cn(styles.dropdownItem, className)}
      onClick={handleClick}
      {...props}
    >
      {children}
    </button>
  );
}
